import React from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { MIN_CONTRACT_DATE } from "utils/date-limits";
import { isRateBelowAbusiveThreshold } from "utils/rate-abusiveness";
import CurrencyTextField from "components/text-fields/currency-text-field";
import PercentageTextField from "components/text-fields/percentage-text-field";

const ContractValuesFields = ({
  contract,
  setContract,
  contractTypes = [],
  isFieldDisabled,
  hasError,
  calculatePgto,
  calculateInstallmentQuestionedValue,
  calculateYearlyInterest,
  calculateDaysDifference,
  onLookupAverageRate,
}) => {
  const isRateNonAbusive = isRateBelowAbusiveThreshold(
    contract.interest_rate_monthly_value,
    contract.interest_rate_average_monthly_value,
    contract.category
  );
  const rateWarningFieldSx = isRateNonAbusive
    ? {
        "& .MuiOutlinedInput-root": {
          "& fieldset": { borderColor: "warning.main" },
          "&:hover fieldset": { borderColor: "warning.dark" },
        },
      }
    : undefined;
  const contractTypeId =
    contract.contract_type && typeof contract.contract_type === "object"
      ? contract.contract_type.id
      : null;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <CurrencyTextField
            label="Valor contratado *"
            value={contract.contracted_value}
            onChange={(value) => {
              setContract({
                ...contract,
                contracted_value: parseFloat(value),
              });
            }}
            disabled={isFieldDisabled("contracted_value")}
            error={hasError("contracted_value")}
            helperText={hasError("contracted_value") ? "Campo obrigatório não preenchido" : undefined}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            label="Parcelas *"
            type="number"
            value={contract.installments}
            error={hasError("installments")}
            helperText={hasError("installments") ? "Campo obrigatório não preenchido" : undefined}
            onChange={(e) => {
              const notQuestionedValue = calculatePgto(
                contract.interest_rate_average_monthly_value / 100,
                e.target.value,
                contract.contract_expenses_value
              );
              setContract({
                ...contract,
                installments: parseInt(e.target.value, 10) || 0,
                installment_part_not_questioned_value: parseFloat(notQuestionedValue),
                installment_questioned_value: parseFloat(
                  calculateInstallmentQuestionedValue(contract.installment_value, notQuestionedValue)
                ),
              });
            }}
            fullWidth
            autoComplete="new-password"
            disabled={isFieldDisabled("installments")}
          />
        </Grid>

        <Grid item xs={4}>
          <CurrencyTextField
            label="Valor das parcelas *"
            value={contract.installment_value}
            onChange={(value) =>
              setContract({
                ...contract,
                installment_value: parseFloat(value),
                installment_questioned_value: parseFloat(
                  calculateInstallmentQuestionedValue(
                    value,
                    contract.installment_part_not_questioned_value
                  )
                ),
              })
            }
            disabled={isFieldDisabled("installment_value")}
            error={hasError("installment_value")}
            helperText={hasError("installment_value") ? "Campo obrigatório não preenchido" : undefined}
          />
        </Grid>

        <Grid item xs={4}>
          <CurrencyTextField
            label="Despesas do contrato (Seguros/IOF)"
            value={contract.contract_expenses_value}
            onChange={(value) => {
              const notQuestionedValue = calculatePgto(
                contract.interest_rate_average_monthly_value / 100,
                contract.installments,
                value
              );
              setContract({
                ...contract,
                contract_expenses_value: parseFloat(value),
                installment_part_not_questioned_value: parseFloat(notQuestionedValue),
                installment_questioned_value: parseFloat(
                  calculateInstallmentQuestionedValue(contract.installment_value, notQuestionedValue)
                ),
              });
            }}
            disabled={isFieldDisabled("contract_expenses_value")}
          />
        </Grid>

        <Grid item xs={4}>
          <CurrencyTextField
            label="Parte parcela não questionada"
            value={contract.installment_part_not_questioned_value}
            onChange={(value) =>
              setContract({
                ...contract,
                installment_part_not_questioned_value: parseFloat(value),
                installment_questioned_value: parseFloat(
                  calculateInstallmentQuestionedValue(contract.installment_value, value)
                ),
              })
            }
            disabled
          />
        </Grid>

        <Grid item xs={4}>
          <CurrencyTextField
            label="Parcela questionada"
            value={contract.installment_questioned_value}
            disabled
          />
        </Grid>

        <Grid item xs={6}>
          <PercentageTextField
            label="Taxa de juros contratada A.M. *"
            value={contract.interest_rate_monthly_value}
            onChange={(value) =>
              setContract({
                ...contract,
                interest_rate_monthly_value: parseFloat(value),
                interest_rate_yearly_value: parseFloat(calculateYearlyInterest(value)),
              })
            }
            disabled={isFieldDisabled("interest_rate_monthly_value")}
            error={hasError("interest_rate_monthly_value")}
            color={isRateNonAbusive && !hasError("interest_rate_monthly_value") ? "warning" : undefined}
            sx={hasError("interest_rate_monthly_value") ? undefined : rateWarningFieldSx}
            helperText={
              hasError("interest_rate_monthly_value")
                ? "Campo obrigatório não preenchido"
                : isRateNonAbusive
                  ? "Abaixo do critério de abusividade (50% acima da média)"
                  : undefined
            }
          />
        </Grid>

        <Grid item xs={6}>
          <PercentageTextField
            label="Taxa de juros contratada A.A."
            value={contract.interest_rate_yearly_value}
            onChange={(value) =>
              setContract({
                ...contract,
                interest_rate_yearly_value: parseFloat(value),
              })
            }
            disabled
            color={isRateNonAbusive ? "warning" : undefined}
            sx={rateWarningFieldSx}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            label="Data da contratação *"
            type="date"
            value={contract.contract_at || ""}
            error={hasError("contract_at")}
            helperText={hasError("contract_at") ? "Campo obrigatório não preenchido" : undefined}
            onChange={(e) => {
              setContract({
                ...contract,
                contract_at: e.target.value,
                grace_period_days: calculateDaysDifference(e.target.value, contract.first_installment_at),
              });
              if (contractTypeId) onLookupAverageRate(contractTypeId, e.target.value);
            }}
            fullWidth
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_CONTRACT_DATE }}
            disabled={isFieldDisabled("contract_at")}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            label="Data primeira parcela *"
            type="date"
            value={contract.first_installment_at || ""}
            error={hasError("first_installment_at")}
            helperText={hasError("first_installment_at") ? "Campo obrigatório não preenchido" : undefined}
            onChange={(e) =>
              setContract({
                ...contract,
                first_installment_at: e.target.value,
                grace_period_days: calculateDaysDifference(contract.contract_at, e.target.value),
              })
            }
            fullWidth
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_CONTRACT_DATE }}
            disabled={isFieldDisabled("first_installment_at")}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Carência"
            value={`${contract.grace_period_days} DIAS`}
            fullWidth
            disabled
            autoComplete="new-password"
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            label="Parcelas pagas *"
            type="number"
            value={contract.installments_payed}
            error={hasError("installments_payed")}
            helperText={hasError("installments_payed") ? "Deve ser maior que zero" : undefined}
            onChange={(e) =>
              setContract({
                ...contract,
                installments_payed: parseInt(e.target.value, 10),
              })
            }
            fullWidth
            autoComplete="new-password"
            disabled={isFieldDisabled("installments_payed")}
          />
        </Grid>

        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={contract.is_renegotiation}
                onChange={(e) =>
                  setContract({
                    ...contract,
                    is_renegotiation: e.target.checked,
                  })
                }
                disabled={isFieldDisabled("is_renegotiation")}
              />
            }
            label="Renegociação?"
          />
        </Grid>

        {contract.is_renegotiation && (
          <>
            <Grid item xs={3}>
              <TextField
                label="Parcelas renegociadas"
                value={contract.renegotiation_installments}
                onChange={(e) =>
                  setContract({
                    ...contract,
                    renegotiation_installments: parseInt(e.target.value, 10) || 0,
                  })
                }
                fullWidth
                disabled={isFieldDisabled("renegotiation_installments")}
              />
            </Grid>
            <Grid item xs={3}>
              <CurrencyTextField
                label="Valor negociação"
                value={contract.renegotiation_value}
                onChange={(value) =>
                  setContract({
                    ...contract,
                    renegotiation_value: value,
                  })
                }
                disabled={isFieldDisabled("renegotiation_value")}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        REFERÊNCIA: Atualização pelo IGP-M e juros de 1% a.m.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            options={contractTypes}
            getOptionLabel={(option) => `${option.code}-${option.name}`}
            value={contractTypes.find((item) => item.id === contract.contract_type?.id) || null}
            onChange={(event, newValue) => {
              setContract({
                ...contract,
                contract_type: newValue || null,
              });
              if (newValue?.id) onLookupAverageRate(newValue.id, contract.contract_at);
            }}
            disabled={isFieldDisabled("contract_type")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de contrato *"
                fullWidth
                autoComplete="new-password"
                error={hasError("contract_type")}
                helperText={hasError("contract_type") ? "Campo obrigatório não preenchido" : undefined}
              />
            )}
          />
        </Grid>

        <Grid item xs={3}>
          <PercentageTextField
            label="Taxa de juros média A.M."
            value={contract.interest_rate_average_monthly_value}
            error={hasError("interest_rate_average_monthly_value")}
            color={isRateNonAbusive && !hasError("interest_rate_average_monthly_value") ? "warning" : undefined}
            sx={hasError("interest_rate_average_monthly_value") ? undefined : rateWarningFieldSx}
            helperText={
              hasError("interest_rate_average_monthly_value")
                ? "Campo obrigatório para o cálculo"
                : isRateNonAbusive
                  ? "Abaixo do critério de abusividade (50% acima da média)"
                  : undefined
            }
            onChange={(value) => {
              const notQuestionedValue = calculatePgto(
                value / 100,
                contract.installments,
                contract.contract_expenses_value
              );
              setContract({
                ...contract,
                interest_rate_average_monthly_value: parseFloat(value),
                interest_rate_average_yearly_value: parseFloat(calculateYearlyInterest(value)),
                installment_part_not_questioned_value: parseFloat(notQuestionedValue),
                installment_questioned_value: parseFloat(
                  calculateInstallmentQuestionedValue(contract.installment_value, notQuestionedValue)
                ),
              });
            }}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <PercentageTextField
            label="A.A."
            value={contract.interest_rate_average_yearly_value}
            disabled
            color={isRateNonAbusive ? "warning" : undefined}
            sx={rateWarningFieldSx}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            label="Atualizado até *"
            type="date"
            value={contract.updated_at || ""}
            error={hasError("updated_at")}
            helperText={hasError("updated_at") ? "Campo obrigatório não preenchido" : undefined}
            onChange={(e) => {
              setContract({
                ...contract,
                updated_at: e.target.value,
              });
            }}
            fullWidth
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_CONTRACT_DATE }}
            disabled={isFieldDisabled("updated_at")}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ContractValuesFields;
